import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import startCase from 'lodash/startCase'
import Layout from 'containers/Layout'
import moment from 'moment'
import { get, orderBy, split, trim, truncate } from 'lodash'
import apiGet from 'lib/network/apiGet'

import { useHistory, useParams, useLocation } from 'react-router-dom'
import Selection from 'components/molecules/Selection'

import Blockies from 'react-blockies'

import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import { startEndTruncate, accessibleOnClick } from 'utils'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip } from 'antd'
import { IoCopyOutline, IoSearch } from 'react-icons/io5'
import notify from 'components/atoms/Notify'
import PageFooter from 'components/paginationComponent'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

const Branches = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const history = useHistory()
  const location = useLocation()

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search)
    return {
      company: params.get('company') || undefined,
      search: params.get('search') || '',
    }
  }

  const initialParams = getQueryParams()
  const [filter, setFilter] = useState({
    type: null,
    workspace: null,
    company: initialParams.company,
  })

  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState([])
  const [pagination, setPagination] = useState({})
  const [search, setSearch] = useState(initialParams.search)
  const [companyNames, setCompanyNames] = useState([])
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  const isSuperCompanyUser = Boolean(userFromStore?.superCompanyUser)
  const canCreateBranch = hasAdminAccess || isSuperCompanyUser

  const { id } = useParams()

  const updateQueryParams = (newFilter, newSearch) => {
    const params = new URLSearchParams()
    if (newFilter.company) params.set('company', newFilter.company)
    if (newSearch) params.set('search', newSearch)
    history.replace({ pathname: '/branches', search: params.toString() })
  }

  useEffect(() => {
    updateQueryParams(filter, search)
    // eslint-disable-next-line
  }, [filter, search])

  useEffect(() => {
    fetchBranches()
  }, [search, filter])

  const fetchBranches = async (page = 1, company = null) => {
    setLoading(true)
    const params = {}
    params.pageNumber = page
    params.pageSize = 100
    params.query = search || undefined
    if (filter.company || company) params.companyId = company || filter.company
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }
    const response = await apiGet('branches', params)

    setBranches(page === 1 ? response.data.branch : [...branches, ...response.data.branch])
    setPagination(response?.data?.pagination || {})

    setLoading(false)
  }

  useEffect(() => {
    if (hasAdminAccess) {
      fetchCompanies()
    }
  }, [hasAdminAccess])

  const fetchCompanies = async () => {
    const response = await apiGet('companies')
    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
  }

  const handleCompanyTypeChange = selectedOption => {
    setFilter(prevFilter => ({
      ...prevFilter,
      company: prevFilter.company === selectedOption?.id ? null : selectedOption?.id,
    }))
  }
  const user = useSelector(state => get(state, 'user.user'))

  const copyText = text => {
    navigator.clipboard.writeText(text).then(() => {
      notify.success(t('branches.messages.copiedToClipboard'), truncate(text, { length: 35 }))
    })
  }

  const navigateToBranchDetail = branchId => {
    const searchParams = new URLSearchParams(location.search)
    const queryString = searchParams.toString()
    const branchDetailPath = queryString ? `/branch/${branchId}/branch-profile?${queryString}` : `/branch/${branchId}/branch-profile`
    history.push(branchDetailPath)
  }

  const columns = [
    {
      name: t('branches.columns.name'),
      width: '10%',
      key: 'name',
      sortable: true,
      rendered: item => {
        return (
          <a
            onClick={e => {
              e.preventDefault()
              navigateToBranchDetail(item._id)
            }}
            className="flex items-center cursor-pointer no-underline"
          >
            {/* <div className="flex-shrink-0 h-10 w-10">
              {item?.logoThumbnailUrl ? (
                <img className="h-10 w-10 rounded-full" src={item?.logoThumbnailUrl} alt="" />
              ) : (
                <Blockies className="rounded-full" seed={item.walletAddress || item.email || ''} size={6.5} scale={6} />
              )}
            </div> */}
            <div className="ml-4">
              <div className="text-sm text-normal text-gray-900">{item?.name || 'User'}</div>
              <div
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                className="text-[13px] text-gray-500 flex"
              ></div>
            </div>
          </a>
        )
      },
    },
    {
      width: '10%',
      name: t('branches.columns.company'),
      key: 'company',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.company?.name}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branches.columns.governorate'),
      key: 'governorate',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.governorate}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branches.columns.email'),
      key: 'email',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.email}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branches.columns.longitude'),
      key: 'lon',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.lon}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branches.columns.latitude'),
      key: 'lat',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.lat}</span>
            </div>
          </>
        )
      },
    },

    {
      width: '5%',
      name: t('branches.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'Registered'
        return createdAt
      },
    },
  ]

  return (
    <Layout
      showLogo={!!id}
      breadcrumbs={[{ name: t('branches.breadcrumb'), pathname: '/branches' }]}
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full justify-end sm:mt-0 sm:ml-4 gap-2 sm:gap-4">
          {(!isCompanyRole || hasAdminAccess) && (
            <Selection
              value={filter.company}
              onChange={handleCompanyTypeChange}
              className="min-w-[200px] w-full sm:w-auto mr-0 sm:mr-4"
              list={companyNames}
              title={t('branches.filters.companyFilter')}
            />
          )}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            <div className="mt-1 relative rounded-md shadow-sm flex-1 sm:flex-none">
              <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                <IoSearch className={`${isRTL ? 'ml-3' : 'mr-3'} h-4 w-4 text-gray-400`} aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="search"
                className={`block w-full ${isRTL ? 'pr-9' : 'pl-9'} shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm`}
                dir={isRTL ? 'rtl' : 'ltr'}
                placeholder={t('branches.filters.searchPlaceholder')}
              />
            </div>
          </div>
          {user.isWorkspaceAdmin
            ? (!isCompanyRole || hasAdminAccess) && (
                <Button onClick={() => history.push('/users/add')} className="w-full sm:w-auto">
                  {t('branches.buttons.inviteUser')}
                </Button>
              )
            : canCreateBranch && (
                <Button onClick={() => history.push('/add/branch')} className="w-full sm:w-auto">
                  {t('branches.buttons.addBranch')}
                </Button>
              )}
        </div>
      }
      title={t('branches.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {branches?.length === 0 ? (
                      <EmptyState v2 description={t('branches.emptyState.description')} />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchBranches(pagination?.currentPage + 1)}
                        sortBy={filter.sortBy}
                        sortOrder={filter.sortOrder}
                        handleSort={(sortBy, sortOrder) => {
                          setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                        }}
                        data={branches}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default Branches
